import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useDemoData } from '../context/DemoDataContext';
import { useGetMetricsQuery } from '../services/dashboardApi';
import { Badge } from './components/ui/badge';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Skeleton } from './components/ui/skeleton';
import { PlanSelectionModal } from './components/PlanSelectionModal';
import { toast } from 'sonner';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  AlertTriangle,
  CheckCircle,
  Activity,
  FileText,
  Plus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}`;

const MetricCard = ({ title, value, icon: Icon, trend, trendValue, color = "primary" }) => (
  <Card className="hover:border-primary/30 transition-colors">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-2xl font-heading font-bold">{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-xs ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
              {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {trendValue}
            </div>
          )}
        </div>
        <div className={`w-10 h-10 rounded-xl bg-${color}/10 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const navigate = useNavigate();
  const { demoMetrics, isDemoMode } = useDemoData();

  const { data: metrics, isLoading } = useGetMetricsQuery();
  const metricsToRender = (metrics && metrics.total_loans > 0) ? metrics : (isDemoMode ? demoMetrics : metrics);
  const isQueryLoading = isLoading && !metricsToRender;

  useEffect(() => {
    if (user && user.hasPlan === false) {
      setShowPlanModal(true);
    }
  }, [user]);

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  if (isQueryLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-80 rounded-xl lg:col-span-2" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      </div>
    );
  }

  const hasData = metricsToRender && metricsToRender.total_loans > 0;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('dashboard.welcome')}, {user?.name || ''}</h1>
          <p className="text-muted-foreground mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => navigate('/clients')}
            className="bg-primary hover:bg-primary/90"
            data-testid="view-clients-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            {t('nav.clients')}
          </Button>
        </div>
      </div>

      {!hasData ? (
        <Card className="p-12 text-center" data-testid="no-data-card">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-primary" />
          </div>
          <h3 className="font-heading text-xl font-semibold mb-2">{t('dashboard.noDataTitle')}</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {t('dashboard.noDataDesc')}
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/clients')} className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              {t('dashboard.addFirstClient')}
            </Button>
          </div>
        </Card>
      ) : (
        <>
          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="hover:border-primary/30 transition-colors" data-testid="metric-total-loans">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('dashboard.totalLoans')}</p>
                    <p className="text-2xl font-heading font-bold">{metricsToRender.total_loans}</p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-green-500">
                      <TrendingUp className="w-3 h-3" />
                      {metricsToRender.active_loans} {t('dashboard.active')}
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/30 transition-colors" data-testid="metric-loan-value">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('dashboard.totalLoanValue')}</p>
                    <p className="text-2xl font-heading font-bold">{formatCurrency(metricsToRender.total_loan_value)}</p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      {t('dashboard.portfolioValue')}
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/30 transition-colors" data-testid="metric-risk-score">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('dashboard.avgRiskScore')}</p>
                    <p className="text-2xl font-heading font-bold">{metricsToRender.average_risk_score}/100</p>
                    <div className={`flex items-center gap-1 mt-2 text-xs ${metricsToRender.average_risk_score < 40 ? 'text-green-500' : metricsToRender.average_risk_score < 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                      <Activity className="w-3 h-3" />
                      {metricsToRender.average_risk_score < 40 ? t('dashboard.lowRisk') : metricsToRender.average_risk_score < 60 ? t('dashboard.mediumRisk') : t('dashboard.highRisk')}
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-primary/30 transition-colors" data-testid="metric-default-rate">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('dashboard.defaultRate')}</p>
                    <p className="text-2xl font-heading font-bold">{metricsToRender.default_rate}%</p>
                    <div className={`flex items-center gap-1 mt-2 text-xs ${metricsToRender.default_rate < 5 ? 'text-green-500' : 'text-red-500'}`}>
                      {metricsToRender.default_rate < 5 ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                      {metricsToRender.default_rate < 5 ? t('dashboard.healthy') : t('dashboard.attentionNeeded')}
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Monthly Trends Chart */}
            <Card className="lg:col-span-2" data-testid="trends-chart">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{t('dashboard.monthlyTrendsTitle')}</CardTitle>
                <CardDescription>{t('dashboard.monthlyTrendsDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {metricsToRender.monthly_trends && metricsToRender.monthly_trends.length > 0 ? (
                  <div style={{ width: '100%', height: 300 }}>
                    <LineChart
                      dataset={metricsToRender.monthly_trends}
                      xAxis={[{ scaleType: 'point', dataKey: 'month' }]}
                      yAxis={[{
                        valueFormatter: (v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`
                      }]}
                      series={[
                        { dataKey: 'loans', label: t('dashboard.loansSeries'), color: '#5B7BB5', showMark: true },
                        { dataKey: 'revenue', label: t('dashboard.revenueSeries'), color: '#E573A0', showMark: true },
                      ]}
                      margin={{ left: 65, right: 30, top: 20, bottom: 40 }}
                      height={280}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-12">{t('dashboard.noTrendData')}</p>
                )}
              </CardContent>
            </Card>

            {/* Risk Distribution */}
            <Card data-testid="risk-distribution-chart">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{t('dashboard.riskDistributionTitle')}</CardTitle>
                <CardDescription>{t('dashboard.riskDistributionDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {metricsToRender.risk_distribution && metricsToRender.risk_distribution.length > 0 ? (
                  <div style={{ width: '100%', height: 260, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <PieChart
                      series={[
                        {
                          data: metricsToRender.risk_distribution.map((item, index) => ({
                            id: index,
                            value: item.value,
                            label: item.name,
                            color: item.color,
                          })),
                          innerRadius: 45,
                          outerRadius: 85,
                          paddingAngle: 3,
                          cornerRadius: 3,
                        },
                      ]}
                      height={240}
                      slotProps={{
                        legend: {
                          direction: 'row',
                          position: { vertical: 'bottom', horizontal: 'middle' },
                          padding: 0,
                        },
                      }}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-12">{t('dashboard.noDistributionData')}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Revenue & Loan Types */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Revenue Summary */}
            <Card data-testid="revenue-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{t('dashboard.revenueSummaryTitle')}</CardTitle>
                <CardDescription>{t('dashboard.revenueSummaryDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-4 rounded-xl bg-muted/50">
                  <span className="text-sm text-muted-foreground">{t('dashboard.totalRevenue')}</span>
                  <span className="font-heading font-bold text-lg">{formatCurrency(metricsToRender.total_revenue)}</span>
                </div>
                <div className="flex justify-between items-center p-4 rounded-xl bg-muted/50">
                  <span className="text-sm text-muted-foreground">{t('dashboard.monthlyRevenue')}</span>
                  <span className="font-heading font-bold text-lg">{formatCurrency(metricsToRender.monthly_revenue)}</span>
                </div>
                <div className="flex justify-between items-center p-4 rounded-xl bg-primary/5 border border-primary/20">
                  <span className="text-sm text-muted-foreground">{t('dashboard.yieldRate')}</span>
                  <span className="font-heading font-bold text-lg text-primary">5.0%</span>
                </div>
              </CardContent>
            </Card>

            {/* Loan Types Chart */}
            <Card data-testid="loan-types-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{t('dashboard.loanTypesTitle')}</CardTitle>
                <CardDescription>{t('dashboard.loanTypesDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {metricsToRender.loan_by_type ? (
                  <div style={{ width: '100%', height: 230 }}>
                    <BarChart
                      dataset={Object.entries(metricsToRender.loan_by_type).map(([name, value]) => ({ name, value }))}
                      xAxis={[{ scaleType: 'band', dataKey: 'name', tickLabelStyle: { fontSize: 10, angle: -15, textAnchor: 'end' } }]}
                      series={[{ dataKey: 'value', label: t('dashboard.countSeries'), color: '#5B7BB5' }]}
                      margin={{ left: 40, right: 20, top: 20, bottom: 55 }}
                      height={220}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-12">{t('dashboard.noLoanTypeData')}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Plan Selection Modal */}
      <PlanSelectionModal
        open={showPlanModal}
        onOpenChange={setShowPlanModal}
      />
    </div>
  );
}
